import { z } from 'zod';
// THE CATEGORY CHARTER — the single declared home of everything a category is
// (the category · catalog · record core chapter; the boundary ADR).
//
// One declaration per category carries: identity (key · trilingual name ·
// section) · structure (the member pair · parent guidance · which products it
// may reference) · the two doors (books · the connectivity three-state, with
// its front posture) · the reference prefix · the catalog coupling (which spec
// columns its products carry) · and
// the EXPECTATIONS row (per fact: followed / quiet / doesn't-apply). Every
// surface — wizard, drawers, peek, detail, columns, import sheets, the nag
// comparators, the future settings surface — reads THIS file through the
// derivation helpers below. No surface hand-wires a category rule.
//
// The laws this file carries (chapter ADR; the letter lives here so the code
// can never drift from it):
//   • CODE VOCABULARY, NO DB CHECKS — adding a category is an everyday code
//     change, never a schema event (the event-type precedent). The DB stores
//     plain text; every write path validates through AssetCategorySchema.
//   • KEYS NEVER LEAVE and never re-mean. Retirement (no case exists yet) will
//     be one additive `offered: false` fact the derivation folds everywhere —
//     pickers stop offering, rows render forever.
//   • A CATEGORY NAMES A BOUGHT THING, never a monitoring subject (no grid
//     rows, no virtual devices — the V2-registry anti-pattern) and never a
//     component that is only catalog knowledge (the engine inside a genset).
//   • BORN ON FIRST REAL NEED, never in advance (cabinet is grandfathered as
//     the last "in case" category). Parked ready-rows, each born the day an
//     estate or a dictionary meets one (birth SHAPE — asset category vs
//     consumable type — decided at birth):
//       `ats` (changeover panels — books on · not networked · genset section ·
//         prefix ATS)
//       `environmental_sensor` (the fuel-sensor twin — books off · not
//         networked · serial followed · prefix ENV · section lean cooling)
//       `wind_turbine_controller` (the controller-pair pattern's one hole —
//         born when a real wind estate shows who reports wind; the channel
//         namespace has no wind domain either, and both enter together)
//       `energy_meter` (utility check-meters / smart meters)
//       `dc_converter`
//       standalone BMS
//   • THREE STATES + LOCKS (the expectations row): `na` = the fact cannot
//     apply — the only thing that ever HIDES (per-entity surfaces omit the
//     field; shared list cells render an em-dash because a column cannot
//     vanish per row) · `quiet` = applies, blank stays silent (peeks hide
//     quiet blanks; edit surfaces still offer the field) · `followed` = a
//     blank renders the word Missing + the family's observation mark — never
//     a block, never hidden. Locks are laws, not preferences (an aggregate
//     that counts nothing AND has no tracked members is incomplete).
//   • REACH, not IP: the followed fact is "a declared way to reach a
//     collectable unit" — an own address OR a via edge satisfies it; `na`
//     means never separately addressed (modules a controller reports,
//     aggregates, panels). `followed` applies only where the unit RESOLVES
//     network-capable: `project override (APP-308, parked) ?? model.
//     is_network_device ?? the connectivity three-state here` (an `na` state
//     resolves false regardless). Push-only transports are exempted when
//     their access kind lands.
//   • The RATING UNIT is derived, never asked (ratingUnitFor — one unit per
//     category from its first spec column; null = the pair is not offered).
//   • Category-dependent rules stay verb-enforced FROM this file — never DB
//     constraints (the DB checks only row-local laws).
//
// Location kinds: EVERY category may sit at a site or in a warehouse (a
// palletized battery string in a depot is honest) — deliberately not a
// per-category fact. There is deliberately NO family/grouping axis beyond the
// SECTION (pure picker/settings grouping — never monitoring domains, which
// arrive at that wave as a different axis with real jobs).
export const ASSET_CATEGORIES = [
    // connectivity
    'gateway',
    // power system — the DC plant's real composition
    'power_system',
    'power_system_controller',
    'rectifier_module',
    'inverter_module',
    'solar_charger',
    'battery_string',
    'battery_module',
    'ups',
    // renewables — the generation field that feeds the plant
    'solar_string',
    'solar_panel',
    'wind_turbine',
    // genset
    'generator',
    'generator_controller',
    'fuel_tank',
    'fuel_sensor',
    // cooling
    'ac_unit',
    'cooling_controller',
    // structure
    'cabinet',
];
export const AssetCategorySchema = z.enum(ASSET_CATEGORIES);
// ── sections — the charter's grouping vocabulary (six, architect-ruled) ──────
export const CATEGORY_SECTION_KEYS = ['connectivity', 'power', 'renewables', 'genset', 'cooling', 'structure'];
export const SECTION_LABELS = {
    connectivity: { en: 'Connectivity', fr: 'Connectivité', ar: 'الاتصال' },
    power: { en: 'Power system', fr: 'Système d’alimentation', ar: 'نظام الطاقة' },
    renewables: { en: 'Renewables', fr: 'Renouvelables', ar: 'الطاقة المتجددة' },
    genset: { en: 'Genset', fr: 'Groupe électrogène', ar: 'المولدات' },
    cooling: { en: 'Cooling', fr: 'Climatisation', ar: 'التبريد' },
    structure: { en: 'Structure', fr: 'Structure', ar: 'الهياكل' },
};
// ── member words (the counting nouns of the aggregate pairs) ─────────────────
export const MEMBER_WORDS = {
    module: {
        en: { one: 'module', other: 'modules' },
        fr: { one: 'module', other: 'modules' },
        ar: { one: 'وحدة', two: 'وحدتان', few: 'وحدات', other: 'وحدة' },
    },
    panel: {
        en: { one: 'panel', other: 'panels' },
        fr: { one: 'panneau', other: 'panneaux' },
        ar: { one: 'لوح', two: 'لوحان', few: 'ألواح', other: 'لوح' },
    },
};
// ── the rating pair (ADR-065 rung 2 — generic-model assets only) ─────────────
// The UNIT IS DERIVED, NEVER ASKED: the category names it (ratingUnitFor); the
// verb layer enforces the match; forms render it as a suffix, never a picker.
export const RATING_UNITS = ['kw', 'kva', 'ah', 'kwp', 'l'];
export const RatingUnitSchema = z.enum(RATING_UNITS);
export const CHARTER_FACTS = ['model', 'serial', 'reach', 'parent', 'condition', 'soh', 'ref_alt'];
const E = (model, serial, reach, parent, condition, soh, refAlt) => ({ model, serial, reach, parent, condition, soh, ref_alt: refAlt });
export const ASSET_CATEGORY_DESCRIPTORS = {
    gateway: {
        key: 'gateway',
        label: { en: 'Gateway', fr: 'Passerelle', ar: 'بوابة اتصال' },
        section: 'connectivity',
        connectivity: 'on',
        frontPosture: 'default-on',
        referencePrefix: 'GW',
        booksDefault: false,
        modelSpecColumns: [],
        expects: E('followed', 'followed', 'followed', 'quiet', 'quiet', 'na', 'quiet'),
    },
    power_system: {
        key: 'power_system',
        label: { en: 'DC power system', fr: 'Système d’alimentation CC', ar: 'نظام طاقة التيار المستمر' },
        section: 'power',
        // The chassis does not speak — the controller is the network face. An
        // integrated smart chassis flips is_network_device on its MODEL.
        connectivity: 'off',
        frontPosture: 'can',
        referencePrefix: 'PS',
        booksDefault: true,
        modelSpecColumns: ['power_kw'],
        expects: E('followed', 'followed', 'followed', 'quiet', 'quiet', 'na', 'quiet'),
    },
    power_system_controller: {
        key: 'power_system_controller',
        label: { en: 'Power system controller', fr: 'Contrôleur du système d’alimentation', ar: 'متحكم نظام الطاقة' },
        section: 'power',
        parentCategories: ['power_system'],
        connectivity: 'on',
        frontPosture: 'can',
        referencePrefix: 'PSC',
        booksDefault: true,
        modelSpecColumns: [],
        expects: E('followed', 'followed', 'followed', 'followed', 'quiet', 'na', 'quiet'),
    },
    rectifier_module: {
        key: 'rectifier_module',
        label: { en: 'Rectifier module', fr: 'Module redresseur', ar: 'وحدة مقوّم' },
        section: 'power',
        parentCategories: ['power_system'],
        // CAN slaves behind the controller — never separately addressed (the
        // who-answers law): reach is structurally na.
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'RM',
        booksDefault: true,
        modelSpecColumns: ['power_kw'],
        expects: E('followed', 'followed', 'na', 'followed', 'quiet', 'na', 'quiet'),
    },
    inverter_module: {
        key: 'inverter_module',
        label: { en: 'Inverter module', fr: 'Module onduleur', ar: 'وحدة عاكس' },
        section: 'power',
        parentCategories: ['power_system'],
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'INV',
        booksDefault: true,
        modelSpecColumns: ['power_kw'],
        expects: E('followed', 'followed', 'na', 'followed', 'quiet', 'na', 'quiet'),
    },
    solar_charger: {
        key: 'solar_charger',
        label: { en: 'Solar charger', fr: 'Chargeur solaire', ar: 'شاحن شمسي' },
        section: 'power',
        parentCategories: ['power_system'],
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'SC',
        booksDefault: true,
        modelSpecColumns: ['power_kw'],
        expects: E('followed', 'followed', 'na', 'followed', 'quiet', 'na', 'quiet'),
    },
    battery_string: {
        key: 'battery_string',
        label: { en: 'Battery string', fr: 'Chaîne de batteries', ar: 'سلسلة بطاريات' },
        section: 'power',
        members: { unit: 'battery_module', word: 'module' },
        modelCategories: ['battery_module', 'battery_string'],
        connectivity: 'na',
        frontPosture: 'na',
        referencePrefix: 'BS',
        booksDefault: true,
        modelSpecColumns: ['capacity_ah', 'voltage_v', 'battery_chemistry', 'battery_technology'],
        // Serial applies-quiet (prepackaged racks + customer SKUs are real); the
        // member count is a LOCK, not an expectation (see isCountIncomplete).
        expects: E('followed', 'quiet', 'na', 'quiet', 'followed', 'followed', 'quiet'),
    },
    battery_module: {
        key: 'battery_module',
        label: { en: 'Battery module', fr: 'Module batterie', ar: 'وحدة بطارية' },
        section: 'power',
        parentCategories: ['battery_string'],
        // A standalone lithium module (no string wrapper) is the correct pattern,
        // so parent stays quiet; smart modules with their own management port are
        // model overrides.
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'BM',
        booksDefault: true,
        modelSpecColumns: ['capacity_ah', 'voltage_v', 'battery_chemistry', 'battery_technology'],
        expects: E('followed', 'followed', 'followed', 'quiet', 'followed', 'followed', 'quiet'),
    },
    ups: {
        key: 'ups',
        // "UPS" in all three languages — the FR word "onduleur" is taken by the
        // inverter module (the onduleur trap, architect-ruled at the names sweep).
        label: { en: 'UPS', fr: 'UPS', ar: 'UPS' },
        section: 'power',
        // The NMC card is optional on every UPS line — smart models declare.
        connectivity: 'off',
        frontPosture: 'can',
        referencePrefix: 'UPS',
        booksDefault: true,
        modelSpecColumns: ['capacity_kva'],
        // No SoH: the internal pack is chassis, not a battery-pair row — its
        // health arrives as telemetry with the UPS as subject; condition covers
        // the human verdict.
        expects: E('followed', 'followed', 'followed', 'quiet', 'quiet', 'na', 'quiet'),
    },
    solar_string: {
        key: 'solar_string',
        label: { en: 'Solar string', fr: 'Chaîne de panneaux', ar: 'سلسلة ألواح' },
        section: 'renewables',
        members: { unit: 'solar_panel', word: 'panel' },
        modelCategories: ['solar_panel'],
        connectivity: 'na',
        frontPosture: 'na',
        referencePrefix: 'SS',
        booksDefault: true,
        // The referenced PANEL products' column (the battery_string pattern):
        // aggregates list their unit product's specs so the headline rating (kWp)
        // and the comparison inputs derive.
        modelSpecColumns: ['watt_peak_w'],
        expects: E('followed', 'quiet', 'na', 'quiet', 'quiet', 'na', 'quiet'),
    },
    solar_panel: {
        key: 'solar_panel',
        label: { en: 'Solar panel', fr: 'Panneau solaire', ar: 'لوح شمسي' },
        section: 'renewables',
        parentCategories: ['solar_string'],
        connectivity: 'na',
        frontPosture: 'na',
        referencePrefix: 'SP',
        // A tracked panel is an identity row born for theft-tracking — the STRING
        // is the priced thing (cheap-numerous principle).
        booksDefault: false,
        modelSpecColumns: ['watt_peak_w'],
        // Parent followed: a deployed tracked panel outside a string is an
        // incomplete composition (the deployed-only law protects depot spares).
        expects: E('followed', 'followed', 'na', 'followed', 'quiet', 'na', 'quiet'),
    },
    wind_turbine: {
        key: 'wind_turbine',
        label: { en: 'Wind turbine', fr: 'Éolienne', ar: 'توربين رياح' },
        section: 'renewables',
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'WT',
        booksDefault: true,
        modelSpecColumns: ['power_kw'],
        expects: E('followed', 'followed', 'followed', 'quiet', 'quiet', 'na', 'quiet'),
    },
    generator: {
        key: 'generator',
        // The bought thing is the genset (engine + alternator + canopy, one
        // purchase); the ENGINE is catalog content linked from the genset record —
        // never a category (additive on a real maintenance/ESN trigger).
        label: { en: 'Generator', fr: 'Groupe électrogène', ar: 'مولّد' },
        section: 'genset',
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'GEN',
        booksDefault: true,
        modelSpecColumns: ['capacity_kva'],
        expects: E('followed', 'followed', 'followed', 'quiet', 'quiet', 'na', 'quiet'),
    },
    generator_controller: {
        key: 'generator_controller',
        label: { en: 'Generator controller', fr: 'Contrôleur de groupe électrogène', ar: 'متحكم المولّد' },
        section: 'genset',
        parentCategories: ['generator'],
        connectivity: 'on',
        frontPosture: 'can',
        referencePrefix: 'GC',
        booksDefault: true,
        modelSpecColumns: [],
        expects: E('followed', 'followed', 'followed', 'followed', 'quiet', 'na', 'quiet'),
    },
    fuel_tank: {
        key: 'fuel_tank',
        // Renamed from diesel_tank (the catalog's channel domain is `fuel`; the
        // register was the straggler). Which fuel THIS tank holds is a future unit
        // fact born with its first actor — withdrawn at the ⚑D gate. Genset BASE
        // tanks are not rows (they ride the genset purchase); only external/day
        // tanks are.
        label: { en: 'Fuel tank', fr: 'Cuve à carburant', ar: 'خزان وقود' },
        section: 'genset',
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'TNK',
        booksDefault: false,
        modelSpecColumns: ['tank_l'],
        expects: E('followed', 'quiet', 'na', 'quiet', 'quiet', 'na', 'quiet'),
    },
    fuel_sensor: {
        key: 'fuel_sensor',
        label: { en: 'Fuel sensor', fr: 'Capteur de carburant', ar: 'حساس الوقود' },
        section: 'genset',
        // A dumb sender wired into the gen controller is reported BY it (no row
        // needed unless managed as a part); the smart lines (Trion FMT — its own
        // SNMP agent on the tank) declare themselves at the model.
        parentCategories: ['fuel_tank', 'generator'],
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'FS',
        booksDefault: false,
        modelSpecColumns: [],
        expects: E('followed', 'followed', 'followed', 'followed', 'quiet', 'na', 'quiet'),
    },
    ac_unit: {
        key: 'ac_unit',
        label: { en: 'AC unit', fr: 'Climatiseur', ar: 'مكيّف' },
        section: 'cooling',
        connectivity: 'off',
        frontPosture: 'na',
        referencePrefix: 'AC',
        booksDefault: true,
        modelSpecColumns: ['cooling_kw'],
        expects: E('followed', 'followed', 'followed', 'quiet', 'quiet', 'na', 'quiet'),
    },
    cooling_controller: {
        key: 'cooling_controller',
        label: { en: 'Cooling controller', fr: 'Contrôleur de climatisation', ar: 'متحكم التبريد' },
        section: 'cooling',
        // NO parent by LAW: the lead-lag brain wall-mounts and manages several AC
        // units — "manages" is monitoring topology, never containment.
        connectivity: 'on',
        frontPosture: 'na',
        referencePrefix: 'CC',
        booksDefault: true,
        modelSpecColumns: [],
        expects: E('followed', 'followed', 'followed', 'na', 'quiet', 'na', 'quiet'),
    },
    cabinet: {
        key: 'cabinet',
        // The quiet row: shelters are usually site-build fabric; estates that
        // track them flip the dials (the settings override is the mechanism).
        label: { en: 'Cabinet', fr: 'Armoire', ar: 'خزانة' },
        section: 'structure',
        connectivity: 'na',
        frontPosture: 'na',
        referencePrefix: 'CAB',
        booksDefault: false,
        modelSpecColumns: [],
        expects: E('quiet', 'quiet', 'na', 'quiet', 'quiet', 'na', 'quiet'),
    },
};
// ── derivation helpers — the ONE derivation every surface reads ──────────────
/** Categories in charter (= display) order, grouped by section. */
export const CATEGORY_SECTIONS = CATEGORY_SECTION_KEYS.map((s) => [s, ASSET_CATEGORIES.filter((c) => ASSET_CATEGORY_DESCRIPTORS[c].section === s)]).filter(([, cats]) => cats.length > 0);
/** The flat category display order — CATEGORY_SECTIONS in reading order. */
export const CATEGORY_DISPLAY_ORDER = CATEGORY_SECTIONS.flatMap(([, cats]) => [...cats]);
/** The category's display name (vocabulary-law inline label). */
export function categoryLabel(category, lang) {
    const label = ASSET_CATEGORY_DESCRIPTORS[category].label;
    const base = lang.split('-')[0];
    return base === 'fr' || base === 'ar' ? label[base] : label.en;
}
/** Model categories an asset of `category` may reference (the aggregate-unit rule). */
export function modelCategoriesFor(category) {
    return ASSET_CATEGORY_DESCRIPTORS[category].modelCategories ?? [category];
}
/** `na` — the networked switch is structurally absent and a model override is
 *  rejected at the verb layer. */
export function isConnectivityNa(category) {
    return ASSET_CATEGORY_DESCRIPTORS[category].connectivity === 'na';
}
/** The category's front posture — birth-panel pre-tick + Via-picker ordering. */
export function frontPostureOf(category) {
    return ASSET_CATEGORY_DESCRIPTORS[category].frontPosture;
}
/** Effective "speaks on the network" for an asset (write gate for the access
 *  facet): project override (APP-308, parked) ?? model override ?? charter.
 *  An `na` category resolves false REGARDLESS of the model — the override is
 *  rejected at write time, and a stray stored one is ignored at read time. */
export function isNetworkCapable(category, modelOverride) {
    const state = ASSET_CATEGORY_DESCRIPTORS[category].connectivity;
    if (state === 'na')
        return false;
    return modelOverride ?? state === 'on';
}
/** The category-level books door (charter default; sparse project overrides
 *  arrive with the observations settings surface). */
export function isBooksCategory(category) {
    return ASSET_CATEGORY_DESCRIPTORS[category].booksDefault;
}
/** A fact's charter state for a category. */
export function factState(category, fact) {
    return ASSET_CATEGORY_DESCRIPTORS[category].expects[fact];
}
/** Categories whose charter FOLLOWS a fact (the nag comparators' arms). */
export function categoriesFollowing(fact) {
    return ASSET_CATEGORIES.filter((c) => ASSET_CATEGORY_DESCRIPTORS[c].expects[fact] === 'followed');
}
/** Categories where a fact APPLIES at all (followed or quiet — everything but na). */
export function categoriesWhereApplies(fact) {
    return ASSET_CATEGORIES.filter((c) => ASSET_CATEGORY_DESCRIPTORS[c].expects[fact] !== 'na');
}
/** Aggregate categories (those with a member pair). */
export const AGGREGATE_CATEGORIES = ASSET_CATEGORIES.filter((c) => ASSET_CATEGORY_DESCRIPTORS[c].members !== undefined);
/** Categories a PRODUCT may belong to — the union of every category's legal
 *  model references. A category no asset may reference (solar_string: strings
 *  are site-built, never SKUs) is structurally orphaned as a product, so the
 *  product surfaces never offer it. */
export const PRODUCT_CATEGORIES = ASSET_CATEGORIES.filter((c) => ASSET_CATEGORIES.some((a) => (ASSET_CATEGORY_DESCRIPTORS[a].modelCategories ?? [a]).includes(c)));
/** Categories whose connectivity is on-by-default (the SQL capability arm's
 *  inputs — an `off` row needs its model override, an `na` row never fires). */
export const NETWORK_DEFAULT_TRUE = ASSET_CATEGORIES.filter((c) => ASSET_CATEGORY_DESCRIPTORS[c].connectivity === 'on');
/** Categories where the networked switch is structurally absent (the SQL arm's
 *  guard — a model override on these is ignored at read time). */
export const CONNECTIVITY_NA_CATEGORIES = ASSET_CATEGORIES.filter((c) => ASSET_CATEGORY_DESCRIPTORS[c].connectivity === 'na');
/** The units LOCK (refined at the walk): an aggregate is incomplete when it
 *  counts nothing AND has no tracked members — a fully-tracked string
 *  legitimately counts zero. */
export function isCountIncomplete(category, unitCount, hasTrackedMembers) {
    return ASSET_CATEGORY_DESCRIPTORS[category].members !== undefined && unitCount == null && !hasTrackedMembers;
}
/** Each numeric spec column's display unit (the battery pair's chemistry /
 *  technology are attributes, never ratings) — the one home for the
 *  spec↔unit pairing; the derived display rating and the generic rating pair
 *  both read it. */
export const SPEC_COLUMN_UNIT = {
    power_kw: 'kw',
    capacity_kva: 'kva',
    capacity_ah: 'ah',
    watt_peak_w: 'kwp',
    cooling_kw: 'kw',
    tank_l: 'l',
};
/** The category's headline rating unit — derived from its first numeric spec
 *  column (one home for the same fact). `null` = the category has no headline
 *  number, so the generic rating pair does not apply there at all (a generic
 *  gateway is complete without a rating; the pair is askable, storable, and
 *  followed ONLY where this returns a unit — ADR-065 rung 2). The unit is
 *  DERIVED, NEVER ASKED: forms render it as a suffix; the verb layer enforces
 *  the match. */
export function ratingUnitFor(category) {
    for (const col of ASSET_CATEGORY_DESCRIPTORS[category].modelSpecColumns) {
        const u = SPEC_COLUMN_UNIT[col];
        if (u)
            return u;
    }
    return null;
}
